import { useEffect, useState } from 'react';
import { chiama, ErroreApi } from '../api/candidatura.js';
import type { Ricerca } from '../api/personale.js';

/**
 * Scegliere cosa finisce nel foglio Excel.
 *
 * È la finestra del v2, con gli stessi gruppi e gli stessi interruttori,
 * comprese le cinque cose che partono spente — i tre impieghi precedenti e le
 * due referenze, che da soli aggiungono quarantatré colonne al foglio.
 *
 * Cambia una cosa: **la scelta resta**. Nel v2 ogni esportazione riparte da
 * capo, e chi ogni lunedì esporta gli stessi otto campi ogni lunedì rispegne
 * gli stessi trenta interruttori.
 *
 * Si esporta quello che si sta guardando: gli stessi filtri dell'elenco.
 */

interface Campo {
  chiave: string;
  etichetta: string;
  predefinito: boolean;
}

interface Gruppo {
  chiave: string;
  titolo: string;
  campi: Campo[];
}

export function Esportazione({ ricerca, onChiudi }: { ricerca: Ricerca; onChiudi: () => void }) {
  const [gruppi, setGruppi] = useState<Gruppo[] | null>(null);
  const [scelte, setScelte] = useState<Record<string, boolean>>({});
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const dati = await chiama<{ groups: Gruppo[]; selected: Record<string, boolean> }>(
          '/api/staff/export/fields',
        );
        setGruppi(dati.groups);
        setScelte(dati.selected);
      } catch (e) {
        setErrore(e instanceof ErroreApi ? e.message : 'Something went wrong.');
      }
    })();
  }, []);

  useEffect(() => {
    const tasto = (e: KeyboardEvent) => e.key === 'Escape' && onChiudi();
    document.addEventListener('keydown', tasto);
    return () => document.removeEventListener('keydown', tasto);
  }, [onChiudi]);

  const quanti = Object.values(scelte).filter(Boolean).length;

  /**
   * Scaricare un file non si fa con `fetch`: il browser deve vederlo arrivare
   * come una risposta di navigazione, o non lo salva. Un modulo che si invia
   * da solo è il modo più semplice, e funziona anche col cookie di sessione.
   */
  const esporta = async () => {
    setInCorso(true);
    setErrore(null);
    try {
      const risposta = await fetch('/api/staff/export', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          fields: scelte,
          q: ricerca.q || undefined,
          status: ricerca.status || undefined,
          withResume: ricerca.withResume || undefined,
        }),
      });

      if (!risposta.ok) {
        const corpo = await risposta.json().catch(() => null);
        throw new ErroreApi('esportazione', corpo?.message ?? 'The export did not work.', risposta.status);
      }

      const blob = await risposta.blob();
      const indirizzo = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = indirizzo;
      // il giorno di chi scarica: in UTC, la sera a New York è già domani
      const oggi = new Date();
      const due = (n: number) => String(n).padStart(2, '0');
      link.download = `applications-${oggi.getFullYear()}-${due(oggi.getMonth() + 1)}-${due(oggi.getDate())}.xlsx`;
      link.click();
      URL.revokeObjectURL(indirizzo);
      onChiudi();
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'The export did not work.');
    } finally {
      setInCorso(false);
    }
  };

  const tuttiDelGruppo = (gruppo: Gruppo, acceso: boolean) =>
    setScelte((s) => {
      const nuove = { ...s };
      for (const campo of gruppo.campi) nuove[campo.chiave] = acceso;
      return nuove;
    });

  return (
    <div className="velo" role="dialog" aria-modal="true" aria-label="Export data">
      <div className="finestra">
        <header className="finestra-testa">
          <h2>Export to Excel</h2>
          <button className="btn-chiudi" onClick={onChiudi} aria-label="Close">
            ✕
          </button>
        </header>

        <div className="finestra-corpo">
          <p className="tenue">
            Choose the fields to export. What you pick is remembered for next time.
          </p>

          {errore && (
            <div className="avviso" role="alert">
              {errore}
            </div>
          )}

          {!gruppi && <p className="tenue">One moment…</p>}

          {gruppi?.map((gruppo) => {
            const accesi = gruppo.campi.filter((c) => scelte[c.chiave]).length;
            return (
              <section key={gruppo.chiave} className="gruppo-campi">
                <div className="gruppo-campi-testa">
                  <h3>{gruppo.titolo}</h3>
                  <button
                    className="btn-testo"
                    onClick={() => tuttiDelGruppo(gruppo, accesi < gruppo.campi.length)}
                  >
                    {accesi < gruppo.campi.length ? 'Select all' : 'Clear'}
                  </button>
                </div>
                <div className="interruttori">
                  {gruppo.campi.map((campo) => (
                    <label key={campo.chiave} className="interruttore">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={scelte[campo.chiave] ?? false}
                        onChange={(e) =>
                          setScelte((s) => ({ ...s, [campo.chiave]: e.target.checked }))
                        }
                      />
                      <span>{campo.etichetta}</span>
                    </label>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <footer className="finestra-piede">
          <span className="tenue">
            {quanti} {quanti === 1 ? 'field' : 'fields'} selected
          </span>
          <div className="finestra-azioni">
            <button className="btn" onClick={onChiudi}>
              Cancel
            </button>
            <button
              className="btn btn-avanti"
              onClick={esporta}
              disabled={inCorso || quanti === 0}
            >
              {inCorso ? 'Preparing…' : 'Export'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
